import { useState, useEffect, useRef } from "react";

// ─── CRANE Works Corporate Site ───
// Design: Refined minimal with warm sophistication
// Colors: Dark charcoal (#3a3a3a) + Teal (#4a9a8e) from logo

const COLORS = {
  charcoal: "#2d2d2d",
  charcoalLight: "#3a3a3a",
  teal: "#4a9a8e",
  tealDark: "#3d8377",
  tealLight: "#5bb5a8",
  cream: "#faf9f7",
  warmWhite: "#f5f3f0",
  warmGray: "#e8e5e1",
  textPrimary: "#2d2d2d",
  textSecondary: "#6b6560",
  textTertiary: "#9a9590",
  border: "#e0dcd7",
};

// ─── Smooth Scroll Hook ───
function useSmoothScroll() {
  return (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
}

// ─── Intersection Observer Hook ───
function useInView(options = {}) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsInView(true); },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, isInView];
}

// ─── AnimatedSection ───
function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 0.8s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// ─── Logo Component ───
function Logo({ size = "default" }) {
  const fontSize = size === "large" ? "2.4rem" : "1.35rem";
  const worksSize = size === "large" ? "2.4rem" : "1.35rem";
  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize, letterSpacing: "-0.03em", lineHeight: 1, display: "flex", alignItems: "baseline", justifyContent: "center", gap: "0.3em", userSelect: "none" }}>
      <span style={{ color: COLORS.charcoal }}>CRANE</span>
      <span style={{ color: COLORS.teal, fontSize: worksSize, fontWeight: 600 }}>Works</span>
    </div>
  );
}

// ─── Navigation ───
function Navigation() {
  const scrollTo = useSmoothScroll();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const navItems = [
    { label: "事業概要", id: "services" },
    { label: "会社概要", id: "company" },
    { label: "お問い合わせ", id: "contact" },
  ];

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .nav-desktop { display: none !important; }
          .nav-hamburger { display: flex !important; }
        }
        @media (min-width: 769px) {
          .nav-desktop { display: flex !important; }
          .nav-hamburger { display: none !important; }
          .nav-mobile-menu { display: none !important; }
        }
      `}</style>
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        padding: scrolled ? "0.8rem 0" : "1.2rem 0",
        backgroundColor: scrolled || menuOpen ? "rgba(250,249,247,0.96)" : "transparent",
        backdropFilter: scrolled || menuOpen ? "blur(20px)" : "none",
        borderBottom: scrolled ? `1px solid ${COLORS.border}` : "1px solid transparent",
        transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
      }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 1.5rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ cursor: "pointer" }} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
            <Logo />
          </div>

          {/* Desktop Nav */}
          <div className="nav-desktop" style={{ gap: "2.5rem", alignItems: "center" }}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 500,
                  color: COLORS.textSecondary, letterSpacing: "0.04em",
                  padding: "0.3rem 0", position: "relative",
                  transition: "color 0.3s",
                }}
                onMouseEnter={(e) => e.target.style.color = COLORS.teal}
                onMouseLeave={(e) => e.target.style.color = COLORS.textSecondary}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Hamburger Button */}
          <button
            className="nav-hamburger"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{
              display: "none", background: "none", border: "none", cursor: "pointer",
              padding: "0.5rem", flexDirection: "column", gap: 5, alignItems: "center", justifyContent: "center",
            }}
          >
            <span style={{ display: "block", width: 22, height: 2, background: COLORS.charcoal, transition: "all 0.3s", transform: menuOpen ? "rotate(45deg) translateY(3.5px)" : "none" }} />
            <span style={{ display: "block", width: 22, height: 2, background: COLORS.charcoal, transition: "all 0.3s", opacity: menuOpen ? 0 : 1 }} />
            <span style={{ display: "block", width: 22, height: 2, background: COLORS.charcoal, transition: "all 0.3s", transform: menuOpen ? "rotate(-45deg) translateY(-3.5px)" : "none" }} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className="nav-mobile-menu"
          style={{
            maxHeight: menuOpen ? 300 : 0, overflow: "hidden",
            transition: "max-height 0.4s cubic-bezier(0.22,1,0.36,1)",
            padding: menuOpen ? "1rem 1.5rem 1.5rem" : "0 1.5rem",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "1.2rem", paddingTop: "0.5rem" }}>
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { scrollTo(item.id); setMenuOpen(false); }}
                style={{
                  background: "none", border: "none", cursor: "pointer", textAlign: "left",
                  fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.95rem", fontWeight: 500,
                  color: COLORS.textSecondary, letterSpacing: "0.04em", padding: "0.3rem 0",
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}

// ─── Hero Section ───
function Hero() {
  const scrollTo = useSmoothScroll();
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <section style={{
      minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center",
      position: "relative", overflow: "hidden",
      background: `linear-gradient(165deg, ${COLORS.cream} 0%, #f0eeeb 40%, #e9efe9 100%)`,
    }}>


      <div style={{
        textAlign: "center", padding: "0 2rem", position: "relative", zIndex: 1,
        opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(40px)",
        transition: "all 1s cubic-bezier(0.22,1,0.36,1) 0.2s",
      }}>
        {/* Tagline above logo */}
        <p style={{
          fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.8rem", fontWeight: 400,
          color: COLORS.textTertiary, letterSpacing: "0.25em", textTransform: "uppercase",
          marginBottom: "2rem",
          opacity: loaded ? 1 : 0, transition: "opacity 0.8s ease 0.6s",
        }}>
          Strategy × Technology
        </p>

        <Logo size="large" />

        <div style={{
          width: 48, height: 2, background: COLORS.teal, margin: "2.5rem auto",
          opacity: loaded ? 1 : 0, transform: loaded ? "scaleX(1)" : "scaleX(0)",
          transition: "all 0.8s cubic-bezier(0.22,1,0.36,1) 0.8s",
        }} />

        <p className="hero-text" style={{
          fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.05rem", fontWeight: 400,
          color: COLORS.textSecondary, lineHeight: 2, maxWidth: 520, margin: "0 auto",
          padding: "0 1rem",
          opacity: loaded ? 1 : 0, transition: "opacity 0.8s ease 1s",
        }}>
          考えを現実にする力。<br />戦略設計から開発まで、事業成長を一気通貫で支援します。
        </p>

        <button
          onClick={() => scrollTo("services")}
          style={{
            marginTop: "3rem", background: "none", border: `1px solid ${COLORS.teal}`,
            color: COLORS.teal, padding: "0.75rem 2.5rem", borderRadius: 0,
            fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.8rem", fontWeight: 500,
            letterSpacing: "0.15em", cursor: "pointer",
            transition: "all 0.3s ease",
            opacity: loaded ? 1 : 0, transitionDelay: "1.2s",
          }}
          onMouseEnter={(e) => {
            e.target.style.background = COLORS.teal;
            e.target.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.target.style.background = "none";
            e.target.style.color = COLORS.teal;
          }}
        >
          事業内容を見る
        </button>
      </div>
    </section>
  );
}

// ─── Services Section ───
function Services() {
  const services = [
    {
      number: "01",
      title: "営業戦略 / 事業戦略支援",
      titleEn: "Sales & Business Strategy",
      description: "市場分析、ターゲティング、KPI設計から実行計画まで、成果に直結する営業・事業戦略を構築します。",
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M4 24L12 12L20 18L28 6" stroke={COLORS.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <circle cx="28" cy="6" r="2.5" fill={COLORS.teal} opacity="0.3"/>
        </svg>
      ),
    },
    {
      number: "02",
      title: "マネジメント代行",
      titleEn: "Management Outsourcing",
      description: "営業組織のマネジメントをプロフェッショナルが代行。チーム育成とKPI達成を両立させます。",
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <circle cx="16" cy="10" r="4" stroke={COLORS.teal} strokeWidth="2"/>
          <circle cx="8" cy="22" r="3" stroke={COLORS.teal} strokeWidth="1.5" opacity="0.5"/>
          <circle cx="24" cy="22" r="3" stroke={COLORS.teal} strokeWidth="1.5" opacity="0.5"/>
          <path d="M16 14V19M12 22H10M20 22H22" stroke={COLORS.teal} strokeWidth="1.5" opacity="0.6"/>
        </svg>
      ),
    },
    {
      number: "03",
      title: "業務標準化 / システム化支援",
      titleEn: "Process Standardization",
      description: "属人化した業務プロセスを可視化・標準化し、スケーラブルな仕組みへ転換します。",
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <rect x="4" y="4" width="10" height="10" rx="2" stroke={COLORS.teal} strokeWidth="2"/>
          <rect x="18" y="4" width="10" height="10" rx="2" stroke={COLORS.teal} strokeWidth="1.5" opacity="0.5"/>
          <rect x="4" y="18" width="10" height="10" rx="2" stroke={COLORS.teal} strokeWidth="1.5" opacity="0.5"/>
          <rect x="18" y="18" width="10" height="10" rx="2" stroke={COLORS.teal} strokeWidth="2"/>
        </svg>
      ),
    },
    {
      number: "04",
      title: "アプリケーション / システム開発",
      titleEn: "Application Development",
      description: "業務課題を解決するWebアプリケーション・業務システムの企画・設計・開発を行います。",
      icon: (
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
          <path d="M10 12L4 16L10 20" stroke={COLORS.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M22 12L28 16L22 20" stroke={COLORS.teal} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M18 6L14 26" stroke={COLORS.teal} strokeWidth="1.5" strokeLinecap="round" opacity="0.5"/>
        </svg>
      ),
    },
  ];

  return (
    <section id="services" style={{ padding: "8rem 2rem", background: COLORS.cream }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <AnimatedSection>
          <div style={{ marginBottom: "5rem" }}>
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: "0.75rem", fontWeight: 500,
              color: COLORS.teal, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "1rem",
            }}>
              Our Services
            </p>
            <h2 className="section-heading" style={{
              fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.8rem", fontWeight: 600,
              color: COLORS.charcoal, letterSpacing: "-0.01em", lineHeight: 1.4,
            }}>
              事業概要
            </h2>
            <div style={{ width: 32, height: 2, background: COLORS.teal, marginTop: "1.5rem" }} />
          </div>
        </AnimatedSection>

        <div className="services-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "2rem" }}>
          {services.map((service, i) => (
            <AnimatedSection key={i} delay={i * 0.12}>
              <ServiceCard service={service} />
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "2.5rem 2rem", background: hovered ? "#fff" : "transparent",
        border: `1px solid ${hovered ? COLORS.border : "transparent"}`,
        transition: "all 0.4s cubic-bezier(0.22,1,0.36,1)",
        cursor: "default", position: "relative",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <span style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: "0.7rem", fontWeight: 600,
          color: COLORS.teal, letterSpacing: "0.1em", opacity: 0.6,
        }}>
          {service.number}
        </span>
        <div style={{ opacity: hovered ? 1 : 0.6, transition: "opacity 0.3s" }}>
          {service.icon}
        </div>
      </div>

      <h3 style={{
        fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.05rem", fontWeight: 600,
        color: COLORS.charcoal, lineHeight: 1.6, marginBottom: "0.4rem",
      }}>
        {service.title}
      </h3>
      <p style={{
        fontFamily: "'DM Sans', sans-serif", fontSize: "0.7rem", fontWeight: 400,
        color: COLORS.teal, letterSpacing: "0.05em", marginBottom: "1.2rem",
      }}>
        {service.titleEn}
      </p>
      <p style={{
        fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 400,
        color: COLORS.textSecondary, lineHeight: 1.9,
      }}>
        {service.description}
      </p>

      <div style={{
        position: "absolute", bottom: 0, left: "2rem", right: "2rem", height: 2,
        background: COLORS.teal,
        transform: hovered ? "scaleX(1)" : "scaleX(0)",
        transformOrigin: "left", transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1)",
      }} />
    </div>
  );
}

// ─── Company Section ───
function Company() {
  const info = [
    { label: "会社名", value: "株式会社CRANE Works（クレインワークス）" },
    { label: "所在地", value: "〒812-0011\n福岡県福岡市博多区博多駅前1丁目23番2号\nParkFront博多駅前1丁目 5F-B" },
    { label: "設立", value: "2025年11月" },
    {
      label: "事業内容",
      value: "営業戦略 / 事業戦略支援\nマネジメント代行\n業務標準化 / システム化支援\nアプリケーション / システム開発",
    },
  ];

  return (
    <section id="company" style={{
      padding: "8rem 2rem",
      background: COLORS.charcoal,
      position: "relative",
    }}>
      {/* Subtle accent line */}
      <div style={{
        position: "absolute", top: 0, left: "50%", width: 1, height: 60,
        background: `linear-gradient(to bottom, ${COLORS.teal}, transparent)`, opacity: 0.4,
      }} />

      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <AnimatedSection>
          <div style={{ marginBottom: "4rem" }}>
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: "0.75rem", fontWeight: 500,
              color: COLORS.teal, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "1rem",
            }}>
              Company
            </p>
            <h2 className="section-heading" style={{
              fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.8rem", fontWeight: 600,
              color: "#fff", letterSpacing: "-0.01em",
            }}>
              会社概要
            </h2>
            <div style={{ width: 32, height: 2, background: COLORS.teal, marginTop: "1.5rem" }} />
          </div>
        </AnimatedSection>

        <div>
          {info.map((item, i) => (
            <AnimatedSection key={i} delay={i * 0.08}>
              <div className="company-row" style={{
                display: "grid", gridTemplateColumns: "140px 1fr",
                borderBottom: `1px solid rgba(255,255,255,0.08)`,
                padding: "1.5rem 0", alignItems: "start",
              }}>
                <span style={{
                  fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.8rem", fontWeight: 500,
                  color: COLORS.teal, letterSpacing: "0.05em",
                }}>
                  {item.label}
                </span>
                <span style={{
                  fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.9rem", fontWeight: 400,
                  color: "rgba(255,255,255,0.8)", lineHeight: 1.9, whiteSpace: "pre-line",
                }}>
                  {item.value}
                </span>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Contact Section ───
function Contact() {
  const [formData, setFormData] = useState({ name: "", email: "", company: "", message: "" });
  const [focusedField, setFocusedField] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const handleSubmit = async () => {
    if (!formData.name || !formData.email || !formData.message) return;
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        alert('送信に失敗しました。もう一度お試しください。');
      }
    } catch (err) {
      alert('送信に失敗しました。もう一度お試しください。');
    }
  };

  const inputStyle = (field) => ({
    width: "100%", padding: "1rem 0", background: "none",
    border: "none", borderBottom: `1px solid ${focusedField === field ? COLORS.teal : COLORS.border}`,
    fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.9rem", color: COLORS.textPrimary,
    outline: "none", transition: "border-color 0.3s",
    boxSizing: "border-box",
  });

  const labelStyle = (field) => ({
    fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.75rem", fontWeight: 500,
    color: focusedField === field ? COLORS.teal : COLORS.textTertiary,
    letterSpacing: "0.08em", transition: "color 0.3s", display: "block", marginBottom: "0.3rem",
  });

  if (submitted) {
    return (
      <section id="contact" style={{ padding: "8rem 2rem", background: COLORS.warmWhite }}>
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", background: COLORS.teal, margin: "0 auto 2rem", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M5 13L9 17L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h3 style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.2rem", fontWeight: 600, color: COLORS.charcoal, marginBottom: "1rem" }}>
            お問い合わせありがとうございます
          </h3>
          <p style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.9rem", color: COLORS.textSecondary, lineHeight: 1.8 }}>
            内容を確認の上、担当者よりご連絡いたします。
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" style={{ padding: "8rem 2rem", background: COLORS.warmWhite }}>
      <div style={{ maxWidth: 600, margin: "0 auto" }}>
        <AnimatedSection>
          <div style={{ marginBottom: "4rem" }}>
            <p style={{
              fontFamily: "'DM Sans', sans-serif", fontSize: "0.75rem", fontWeight: 500,
              color: COLORS.teal, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "1rem",
            }}>
              Contact
            </p>
            <h2 className="section-heading" style={{
              fontFamily: "'Noto Sans JP', sans-serif", fontSize: "1.8rem", fontWeight: 600,
              color: COLORS.charcoal,
            }}>
              お問い合わせ
            </h2>
            <div style={{ width: 32, height: 2, background: COLORS.teal, marginTop: "1.5rem" }} />
            <p style={{
              fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 400,
              color: COLORS.textSecondary, lineHeight: 1.9, marginTop: "1.5rem",
            }}>
              サービスに関するご相談やお見積もりなど、<br />お気軽にお問い合わせください。
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.15}>
          <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
            <div className="contact-name-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2rem" }}>
              <div>
                <label style={labelStyle("name")}>お名前 *</label>
                <input
                  type="text" value={formData.name} onChange={handleChange("name")}
                  onFocus={() => setFocusedField("name")} onBlur={() => setFocusedField(null)}
                  style={inputStyle("name")} placeholder="山田 太郎"
                />
              </div>
              <div>
                <label style={labelStyle("company")}>会社名</label>
                <input
                  type="text" value={formData.company} onChange={handleChange("company")}
                  onFocus={() => setFocusedField("company")} onBlur={() => setFocusedField(null)}
                  style={inputStyle("company")} placeholder="株式会社○○"
                />
              </div>
            </div>

            <div>
              <label style={labelStyle("email")}>メールアドレス *</label>
              <input
                type="email" value={formData.email} onChange={handleChange("email")}
                onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)}
                style={inputStyle("email")} placeholder="info@example.com"
              />
            </div>

            <div>
              <label style={labelStyle("message")}>お問い合わせ内容 *</label>
              <textarea
                value={formData.message} onChange={handleChange("message")}
                onFocus={() => setFocusedField("message")} onBlur={() => setFocusedField(null)}
                rows={5}
                style={{
                  ...inputStyle("message"), resize: "vertical", lineHeight: 1.8,
                }}
                placeholder="ご相談内容をご記入ください"
              />
            </div>

            <button
              onClick={handleSubmit}
              style={{
                marginTop: "1rem", padding: "1rem 3rem",
                background: COLORS.teal, color: "#fff", border: "none",
                fontFamily: "'Noto Sans JP', sans-serif", fontSize: "0.85rem", fontWeight: 500,
                letterSpacing: "0.1em", cursor: "pointer",
                transition: "all 0.3s", alignSelf: "flex-start",
              }}
              onMouseEnter={(e) => e.target.style.background = COLORS.tealDark}
              onMouseLeave={(e) => e.target.style.background = COLORS.teal}
            >
              送信する
            </button>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

// ─── Footer ───
function Footer() {
  return (
    <footer style={{
      padding: "3rem 2rem", background: COLORS.charcoal,
      borderTop: `1px solid rgba(255,255,255,0.06)`,
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: "0.85rem", fontWeight: 600,
          color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em",
        }}>
          CRANE Works Inc.
        </p>
        <p style={{
          fontFamily: "'DM Sans', sans-serif", fontSize: "0.7rem", fontWeight: 400,
          color: "rgba(255,255,255,0.3)", letterSpacing: "0.05em",
        }}>
          &copy; 2026 CRANE Works Inc. All rights reserved.
        </p>
      </div>
    </footer>
  );
}

// ─── Main App ───
export default function CraneWorksSite() {
  return (
    <div style={{ fontFamily: "'Noto Sans JP', 'DM Sans', sans-serif", WebkitFontSmoothing: "antialiased" }}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=Noto+Sans+JP:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        @media (max-width: 768px) {
          .hero-text br { display: none; }
          .hero-heading { font-size: 1.8rem !important; }
          .section-heading { font-size: 1.4rem !important; }
          .services-grid { grid-template-columns: 1fr !important; }
          .company-row { grid-template-columns: 1fr !important; gap: 0.3rem !important; }
          .contact-name-row { grid-template-columns: 1fr !important; }
        }
        @media (min-width: 769px) and (max-width: 1024px) {
          .services-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
      `}</style>
      <Navigation />
      <Hero />
      <Services />
      <Company />
      <Contact />
      <Footer />
    </div>
  );
}
import { createRoot } from "react-dom/client";

const root = createRoot(document.getElementById("root"));
root.render(<CraneWorksSite />);
